import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ModalDefaultDraggable } from '../../../molecules';
import { Loader } from '../../../atoms';
import { useGetJurisdictionsByStateQuery } from '../../../redux/rtkQuery/apiWikiSlice';
import { useSaveWikiKesMutation, useSearchWikiKeysQuery, useUpdateWikiKeyMutation } from '../../../redux/rtkQuery/apiWikiKeysSlice';
import { useSearchClientsQuery } from '../../../redux/rtkQuery/apiSlice';
import RichTextEditor, { isRichTextEmpty } from '../SharedComponents/RichTextEditor';

type Props = {
  row?: any;
  onClose: () => void;
};

const AddCredentialModal = ({ row, onClose }: Props) => {
  const [jurisdictionId, setJurisdictionId] = useState('');
  const [login, setLogin] = useState('');
  const [key, setKey] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [selectedClient, setSelectedClient] = useState<any>(null);
  const [jurisdictionSearch, setJurisdictionSearch] = useState('');
  const [showJurisdictionResults, setShowJurisdictionResults] = useState(false);

  const effectiveRow = row || selectedClient;

  const shouldSearchClients = !row && !selectedClient && clientSearch.trim().length >= 3;
  const { data: clientResults, isFetching: isSearchingClients } = useSearchClientsQuery(
    { token: clientSearch.trim() },
    { skip: !shouldSearchClients },
  );

  const { data: jurisdictions, isFetching: isLoadingJurisdictions } = useGetJurisdictionsByStateQuery('FL');
  const [saveWikiKey] = useSaveWikiKesMutation();
  const [updateWikiKey] = useUpdateWikiKeyMutation();

  // Check if this client already has a key for the selected jurisdiction
  const searchParams = jurisdictionId && effectiveRow
    ? effectiveRow.clientType === 2
      ? { jurisdiction_id: jurisdictionId, company_name: effectiveRow.companyName }
      : { jurisdiction_id: jurisdictionId, contact_id: effectiveRow.userId }
    : undefined;
  const { data: existingKeys } = useSearchWikiKeysQuery(searchParams, { skip: !searchParams });
  const existingKey = existingKeys?.results?.[0] || null;
  const isEditMode = !!existingKey;

  const selectedJurisdiction = jurisdictions?.find((j: any) => String(j.id) === jurisdictionId);
  const portalLink = selectedJurisdiction?.permitting?.portalLink || '';

  useEffect(() => {
    if (existingKey) {
      setLogin(existingKey.username || '');
      setKey(existingKey.password || '');
      setNotes(existingKey.notes || '');
    } else {
      setLogin('');
      setKey('');
      setNotes('');
    }
  }, [existingKey]);

  useEffect(() => {
    setError('');
  }, [jurisdictionId, login, key, notes]);

  const onSubmit = async () => {
    if (!effectiveRow) {
      setError('Please select a client');
      return;
    }
    if (!jurisdictionId || !login || !key) {
      setError('All fields are required');
      return;
    }

    try {
      const notesValue = isRichTextEmpty(notes) ? '' : notes;

      if (isEditMode) {
        const formData = new FormData();
        formData.append('username', login);
        formData.append('password', key);
        formData.append('notes', notesValue);
        await updateWikiKey({ id: existingKey.id, frm: formData }).unwrap();
      } else {
        const formData = new FormData();
        if (effectiveRow.clientType === 2) {
          formData.append('company_name', effectiveRow.companyName);
        } else {
          formData.append('contact', `${effectiveRow.userId}`);
        }
        formData.append('jurisdiction', jurisdictionId);
        formData.append('username', login);
        formData.append('password', key);
        formData.append('notes', notesValue);
        await saveWikiKey(formData).unwrap();
      }
      onClose();
    } catch (e) {
      setError(isEditMode ? 'Failed to update credential' : 'Failed to save credential');
    }
  };

  const handleSelectClient = (client: any) => {
    // Convert searchClients result shape to the row shape used internally:
    // { firstName, lastName, companyName, clientType, userId }
    const labelParts = (client.label || '').split(' ');
    setSelectedClient({
      firstName: labelParts[0] || '',
      lastName: labelParts.slice(1).join(' '),
      companyName: client.companyName || '',
      clientType: client.clientType,
      userId: client.value,
    });
    setClientSearch(client.label || client.companyName || '');
  };

  const clearSelectedClient = () => {
    setSelectedClient(null);
    setClientSearch('');
    setJurisdictionId('');
    setJurisdictionSearch('');
    setLogin('');
    setKey('');
    setNotes('');
  };

  const handleSelectJurisdiction = (j: any) => {
    setJurisdictionId(`${j.id}`);
    setJurisdictionSearch(j.jurisdictions);
    setShowJurisdictionResults(false);
  };

  const handleJurisdictionInput = (value: string) => {
    setJurisdictionSearch(value);
    setShowJurisdictionResults(true);
    if (jurisdictionId) {
      // user is editing the search; clear current selection so they can re-pick
      setJurisdictionId('');
    }
  };

  const filteredJurisdictions = (() => {
    const all = [...(jurisdictions || [])].sort((a: any, b: any) =>
      a.jurisdictions.localeCompare(b.jurisdictions),
    );
    const q = jurisdictionSearch.trim().toLowerCase();
    if (!q) return all;
    return all.filter((j: any) => (j.jurisdictions || '').toLowerCase().includes(q));
  })();

  const getClientDisplay = () => {
    if (!effectiveRow) return '';
    const name = [effectiveRow.firstName, effectiveRow.lastName].filter(Boolean).join(' ');
    return [name, effectiveRow.companyName].filter(Boolean).join(' - ') || '';
  };

  return (
    <StyledModal onClose={onClose} title="Add New Credential">
      <Form>
        {/* EA-1200: Client, Jurisdiction, Login and Key are mandatory. */}
        <FieldLabel>Client<RequiredStar>*</RequiredStar></FieldLabel>
        {row ? (
          <ClientName>{getClientDisplay()}</ClientName>
        ) : selectedClient ? (
          <SelectedClientRow>
            <ClientName>{getClientDisplay()}</ClientName>
            <ChangeClientButton type="button" onClick={clearSelectedClient}>Change</ChangeClientButton>
          </SelectedClientRow>
        ) : (
          <ClientSearchWrapper>
            <Input
              type="text"
              value={clientSearch}
              onChange={e => setClientSearch(e.target.value)}
              placeholder="Search client name..."
              autoFocus
            />
            {clientSearch.trim().length > 0 && clientSearch.trim().length < 3 && (
              <SearchHint>Type at least 3 characters to search</SearchHint>
            )}
            {shouldSearchClients && (
              <SearchResults>
                {isSearchingClients ? (
                  <LoadingRow>
                    <Loader size={18} />
                    <span>Searching...</span>
                  </LoadingRow>
                ) : (clientResults && clientResults.length > 0) ? (
                  clientResults.map((c: any) => (
                    <SearchResultItem key={c.value} onClick={() => handleSelectClient(c)}>
                      <ResultName>{c.label || c.companyName || ''}</ResultName>
                      <ResultMeta>
                        {[c.email, c.companyName].filter(Boolean).join(' - ')}
                      </ResultMeta>
                    </SearchResultItem>
                  ))
                ) : (
                  <NoResults>No clients found</NoResults>
                )}
              </SearchResults>
            )}
          </ClientSearchWrapper>
        )}

        {effectiveRow && (
          <>
            <FieldLabel>Jurisdiction<RequiredStar>*</RequiredStar></FieldLabel>
            {isLoadingJurisdictions ? (
              <LoadingRow>
                <Loader size={18} />
                <span>Loading jurisdictions...</span>
              </LoadingRow>
            ) : (
              <JurisdictionSearchWrapper>
                <Input
                  type="text"
                  value={jurisdictionSearch}
                  onChange={e => handleJurisdictionInput(e.target.value)}
                  onFocus={() => setShowJurisdictionResults(true)}
                  onBlur={() => setTimeout(() => setShowJurisdictionResults(false), 150)}
                  placeholder="Search jurisdiction..."
                />
                {showJurisdictionResults && (
                  <SearchResults>
                    {filteredJurisdictions.length > 0 ? (
                      filteredJurisdictions.map((j: any) => (
                        <SearchResultItem
                          key={j.id}
                          onMouseDown={() => handleSelectJurisdiction(j)}
                        >
                          <ResultName>{j.jurisdictions}</ResultName>
                        </SearchResultItem>
                      ))
                    ) : (
                      <NoResults>No jurisdictions found</NoResults>
                    )}
                  </SearchResults>
                )}
              </JurisdictionSearchWrapper>
            )}

            {jurisdictionId && (
              <>
                <FieldLabel>Link to portal</FieldLabel>
                {portalLink ? (
                  <PortalLink href={portalLink} target="_blank" rel="noopener noreferrer">
                    {portalLink}
                  </PortalLink>
                ) : (
                  <NoPortal>portal url not setup</NoPortal>
                )}
              </>
            )}

            <FieldLabel>Login<RequiredStar>*</RequiredStar></FieldLabel>
            <Input
              type="text"
              value={login}
              onChange={e => setLogin(e.target.value)}
              placeholder=""
            />

            <FieldLabel>Key<RequiredStar>*</RequiredStar></FieldLabel>
            <Input
              type="text"
              value={key}
              onChange={e => setKey(e.target.value)}
              placeholder=""
            />

            <FieldLabel>Notes</FieldLabel>
            <RichTextEditor
              value={notes}
              onChange={setNotes}
              placeholder="Add notes for this credential..."
            />
          </>
        )}

        {error && (
          <ErrorText>
            {error === 'All fields are required' ? (
              <>
                All <RequiredStar>*</RequiredStar> fields are required
              </>
            ) : (
              error
            )}
          </ErrorText>
        )}

        {effectiveRow && (
          <AddButton onClick={onSubmit}>{isEditMode ? 'UPDATE' : 'ADD'}</AddButton>
        )}
      </Form>
    </StyledModal>
  );
};

export default AddCredentialModal;

const LoadingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 0;
  font-size: 13px;
  color: var(--defaultTableCellTitle);
`;

const StyledModal = styled(ModalDefaultDraggable)`
  overflow-y: auto;
  text-align: center;
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px 20px 20px;
  gap: 6px;
  text-align: left;
`;

const ClientName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: var(--mainText);
  padding: 4px 0;
`;

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: var(--primary);
  margin-top: 10px;
`;

// EA-1200: red required indicator for mandatory fields.
const RequiredStar = styled.span`
  color: var(--danger-500, #dc2626);
  margin-left: 2px;
`;

const PortalLink = styled.a`
  font-size: 13px;
  color: var(--primary);
  text-decoration: underline;
  word-break: break-all;

  &:hover {
    opacity: 0.8;
  }
`;

const NoPortal = styled.span`
  font-size: 13px;
  color: var(--defaultTableCellTitle);
  font-style: italic;
`;

const Input = styled.input`
  width: 100%;
  height: 36px;
  padding: 4px 8px;
  border: 1px solid var(--border);
  border-radius: 4px;
  font-size: 13px;
  box-sizing: border-box;
`;

const ErrorText = styled.div`
  color: red;
  font-size: 12px;
  text-align: center;
  margin-top: 4px;
`;

const AddButton = styled.button`
  background-color: var(--primary);
  color: #fff;
  border: none;
  padding: 8px 32px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 3px;
  margin-top: 16px;
  align-self: center;

  &:hover {
    opacity: 0.9;
  }
`;

const ClientSearchWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const JurisdictionSearchWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const SearchHint = styled.div`
  font-size: 12px;
  color: var(--neutral-500);
  margin-top: 4px;
`;

const SearchResults = styled.div`
  margin-top: 4px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background-color: #fff;
  max-height: 220px;
  overflow-y: auto;
`;

const SearchResultItem = styled.div`
  padding: 8px 10px;
  cursor: pointer;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background-color: var(--neutral-50);
  }
`;

const ResultName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--mainText);
`;

const ResultMeta = styled.div`
  font-size: 12px;
  color: var(--neutral-500);
  margin-top: 2px;
`;

const NoResults = styled.div`
  padding: 12px;
  text-align: center;
  font-size: 13px;
  color: var(--neutral-500);
`;

const SelectedClientRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const ChangeClientButton = styled.button`
  height: 28px;
  padding: 0 12px;
  background-color: transparent;
  color: var(--primary);
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background-color: var(--neutral-50);
  }
`;
